import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Users2, Plus, Pencil, Trash2, FilePlus2, ChevronDown, ChevronUp, Send } from "lucide-react";
import { toast } from "sonner";
import { CommitteeTasksTab } from "@/components/CommitteeTasksTab";

export default function CommitteesPage() {
  const { properties, propertyId } = useProperty();
  const [scope, setScope] = useState("all");
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", property_id: "", description: "", chair_id: "", member_ids: [] });
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [meetings, setMeetings] = useState({});
  const [meetingDialog, setMeetingDialog] = useState(null); // { committee_id }
  const [meetingForm, setMeetingForm] = useState({ title: "", date: new Date().toISOString().slice(0, 10), notes: "" });

  const load = useCallback(async () => {
    try {
      const params = {};
      if (scope !== "all") params.property_id = scope;
      const { data } = await api.get("/admin/committees", { params });
      setItems(data.items || []);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to load committees"); }
  }, [scope]);

  const loadUsers = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/users", { params: { include_inactive: false } });
      setUsers(data.items || []);
    } catch (err) { if (typeof console !== 'undefined') console.debug('pages/admin/CommitteesPage.jsx: swallowed', err); }
  }, []);

  useEffect(() => { load(); loadUsers(); }, [load, loadUsers]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", property_id: scope !== "all" ? scope : (propertyId || properties[0]?.id || ""),
              description: "", chair_id: "", member_ids: [], auto_publish_minutes: false });
    setDialogOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, property_id: c.property_id, description: c.description || "",
              chair_id: c.chair_id || "", member_ids: c.member_ids || [],
              auto_publish_minutes: !!c.auto_publish_minutes });
    setDialogOpen(true);
  };

  const toggleMember = (uid) => setForm((f) => ({
    ...f,
    member_ids: f.member_ids.includes(uid) ? f.member_ids.filter((x) => x !== uid) : [...f.member_ids, uid],
  }));

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.chair_id) delete payload.chair_id;
      if (!payload.description) delete payload.description;
      if (editing) {
        const { property_id, ...upd } = payload;
        await api.patch(`/admin/committees/${editing.id}`, upd);
        toast.success("Committee updated");
      } else {
        await api.post("/admin/committees", payload);
        toast.success("Committee created");
      }
      setDialogOpen(false);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Save failed"); }
    finally { setBusy(false); }
  };

  const archive = async (c) => {
    if (!window.confirm(`Archive "${c.name}"?`)) return;
    try {
      await api.delete(`/admin/committees/${c.id}`);
      toast.success("Archived");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Archive failed"); }
  };

  const toggleExpand = async (c) => {
    setExpanded((prev) => ({ ...prev, [c.id]: !prev[c.id] }));
    if (!meetings[c.id]) {
      try {
        const { data } = await api.get(`/admin/committees/${c.id}/meetings`);
        setMeetings((m) => ({ ...m, [c.id]: data.items || [] }));
      } catch (err) { if (typeof console !== 'undefined') console.debug('pages/admin/CommitteesPage.jsx: swallowed', err); }
    }
  };

  const openMeetingDialog = (c) => {
    setMeetingDialog({ committee: c });
    setMeetingForm({ title: "", date: new Date().toISOString().slice(0, 10), notes: "" });
  };

  const submitMeeting = async () => {
    setBusy(true);
    try {
      const c = meetingDialog.committee;
      await api.post(`/admin/committees/${c.id}/meetings`, { ...meetingForm, attendee_ids: c.member_ids || [] });
      toast.success("Meeting note added");
      setMeetingDialog(null);
      const { data } = await api.get(`/admin/committees/${c.id}/meetings`);
      setMeetings((m) => ({ ...m, [c.id]: data.items || [] }));
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to save"); }
    finally { setBusy(false); }
  };

  const toggleAutoPublish = async (c) => {
    // R163 — inline switch on the committee card.  Optimistic update so
    // the flip feels instant; rolls back on error.
    const next = !c.auto_publish_minutes;
    setItems((prev) => prev.map((x) =>
      x.id === c.id ? { ...x, auto_publish_minutes: next } : x));
    try {
      await api.patch(`/admin/committees/${c.id}`,
                       { auto_publish_minutes: next });
      toast.success(next
        ? "Auto-publish on — minutes older than 24 h will publish themselves"
        : "Auto-publish off — chair will need to click Publish");
    } catch (e) {
      setItems((prev) => prev.map((x) =>
        x.id === c.id ? { ...x, auto_publish_minutes: !next } : x));
      toast.error(apiErrorMessage(e) || "Toggle failed");
    }
  };

  const deleteMeeting = async (cid, mid) => {
    if (!window.confirm("Delete this meeting note?")) return;
    try {
      await api.delete(`/admin/committees/${cid}/meetings/${mid}`);
      setMeetings((m) => ({ ...m, [cid]: (m[cid] || []).filter((x) => x.id !== mid) }));
    } catch (e) { toast.error(apiErrorMessage(e) || "Delete failed"); }
  };

  const publishMeeting = async (cid, m) => {
    // R160 — one-click publish to the community Document Library.
    const already = !!m.published_document_id;
    const verb = already ? "Re-publish" : "Publish";
    if (!window.confirm(
      `${verb} "${m.title}" to the community Document Library?\n\n`
      + "The minutes are rendered as a PDF and saved to the "
      + "\"Committee Minutes\" folder (auto-created if missing). "
      + "Board members can see it immediately."
    )) return;
    try {
      const { data } = await api.post(
        `/admin/committees/${cid}/meetings/${m.id}/publish`,
        { visibility: "board", folder_name: "Committee Minutes" }
      );
      toast.success(
        data.updated
          ? `Re-published (${Math.round(data.size / 1024)} KB)`
          : `Published to ${data.folder_name} · ${Math.round(data.size / 1024)} KB`
      );
      const { data: d2 } = await api.get(`/admin/committees/${cid}/meetings`);
      setMeetings((s) => ({ ...s, [cid]: d2.items || [] }));
    } catch (e) { toast.error(apiErrorMessage(e) || "Publish failed"); }
  };

  const publishAll = async (c) => {
    // R161 — batch-publish every unpublished meeting for this committee.
    const unpub = (meetings[c.id] || []).filter((m) => !m.published_document_id).length;
    if (unpub === 0) {
      toast.info("No unpublished minutes — the backlog is already caught up.");
      return;
    }
    if (!window.confirm(
      `Publish all ${unpub} unpublished meeting minute${unpub === 1 ? "" : "s"} for "${c.name}"?\n\n`
      + "Each meeting will be rendered as its own PDF and dropped into the "
      + "\"Committee Minutes\" folder. Board members can see them immediately."
    )) return;
    try {
      const { data } = await api.post(
        `/admin/committees/${c.id}/publish-all`,
        { visibility: "board", folder_name: "Committee Minutes" }
      );
      const fresh = data.published || 0;
      const errs = data.errors || 0;
      toast.success(
        `${fresh} published · ${data.skipped || 0} skipped`
        + (errs ? ` · ${errs} error${errs === 1 ? "" : "s"}` : "")
      );
      const { data: d2 } = await api.get(`/admin/committees/${c.id}/meetings`);
      setMeetings((s) => ({ ...s, [c.id]: d2.items || [] }));
    } catch (e) { toast.error(apiErrorMessage(e) || "Batch publish failed"); }
  };

  const usersInScope = useMemo(() => {
    if (!form.property_id) return users;
    return users.filter((u) => u.role === "admin" || (u.property_ids || []).includes(form.property_id));
  }, [users, form.property_id]);

  return (
    <div>
      <PageHeader eyebrow="Governance" title="Committees" subtitle="Standing groups of residents and board members — ARC, social, budget, landscape, whatever your community needs." />

      <div className="flex items-center gap-2 mb-4">
        <Button onClick={openCreate} className="bg-orange-500 hover:bg-orange-600 rounded-full" data-testid="committee-add-btn">
          <Plus className="h-4 w-4 mr-1" /> New committee
        </Button>
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger className="w-48 h-9 bg-white/[0.04] border-white/10" data-testid="committee-scope"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
            <SelectItem value="all">All communities</SelectItem>
            {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto text-xs text-zinc-500">{items.length} committee{items.length === 1 ? "" : "s"}</div>
      </div>

      {items.length === 0 ? (
        <Card className="p-8 text-center border-white/10" data-testid="committees-empty">
          <Users2 className="h-6 w-6 mx-auto text-zinc-500 mb-2" />
          <div className="text-sm text-zinc-400">No committees yet. Click <b>New committee</b> to add one.</div>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((c) => (
            <Card key={c.id} className="border-white/10" data-testid={`committee-card-${c.id}`}>
              <div className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <Users2 className="h-4 w-4 text-orange-400" />
                      <div className="text-base font-semibold text-zinc-100">{c.name}</div>
                      <Badge variant="outline" className="border-white/10 text-zinc-400 text-[10px]">
                        {properties.find((p) => p.id === c.property_id)?.name || c.property_id}
                      </Badge>
                    </div>
                    {c.description && <div className="text-xs text-zinc-500 mt-1">{c.description}</div>}
                    <div className="mt-2 flex flex-wrap gap-1">
                      {c.chair && <Badge variant="outline" className="border-orange-500/40 text-orange-300 text-[10px]">Chair · {c.chair.name}</Badge>}
                      {(c.members || []).map((m) => (
                        <Badge key={m.id} variant="outline" className="border-white/10 text-zinc-300 text-[10px]">{m.name}</Badge>
                      ))}
                      {(c.members || []).length === 0 && !c.chair && (
                        <span className="text-[11px] text-zinc-600 italic">No members yet</span>
                      )}
                    </div>
                    {/* R163 — Auto-publish minutes toggle */}
                    <div className="mt-2.5 flex items-center gap-2">
                      <label
                        htmlFor={`auto-publish-${c.id}`}
                        className="text-[11px] text-zinc-400 cursor-pointer select-none flex items-center gap-1.5"
                        title="When on, meetings sitting unpublished for 24 h are rendered to PDF and dropped into the Documents library automatically."
                      >
                        <span>Auto-publish minutes after 24 h</span>
                      </label>
                      <button
                        id={`auto-publish-${c.id}`}
                        onClick={() => toggleAutoPublish(c)}
                        aria-pressed={!!c.auto_publish_minutes}
                        role="switch"
                        data-testid={`committee-auto-publish-${c.id}`}
                        className={"relative h-4 w-8 rounded-full transition-colors "
                          + (c.auto_publish_minutes
                              ? "bg-emerald-500/70"
                              : "bg-white/10 dark:bg-white/10")}
                      >
                        <span
                          className={"absolute top-0.5 h-3 w-3 rounded-full bg-white transition-transform "
                            + (c.auto_publish_minutes ? "translate-x-4" : "translate-x-0.5")}
                        />
                      </button>
                      {c.auto_publish_minutes && (
                        <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 text-[9px]">
                          Auto
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => openEdit(c)} className="border-white/10" data-testid={`committee-edit-${c.id}`}>
                      <Pencil className="h-3 w-3 mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openMeetingDialog(c)} className="text-indigo-300 hover:text-indigo-200"
                            data-testid={`committee-add-meeting-${c.id}`}>
                      <FilePlus2 className="h-3 w-3 mr-1" /> Meeting
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => archive(c)} className="text-zinc-400 hover:text-red-300" data-testid={`committee-archive-${c.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                    <button onClick={() => toggleExpand(c)} className="text-zinc-500 hover:text-zinc-300 p-1" data-testid={`committee-expand-${c.id}`}>
                      {expanded[c.id] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {expanded[c.id] && (
                  <div className="mt-3 border-t border-white/5 pt-3">
                    <CommitteeTasksTab committee={c} />
                    <div className="mb-2 mt-4 flex items-center justify-between">
                      <div className="text-[11px] uppercase tracking-widest text-zinc-500">Meetings</div>
                      {(() => {
                        const unpub = (meetings[c.id] || []).filter((mm) => !mm.published_document_id).length;
                        return unpub > 0 ? (
                          <button
                            onClick={() => publishAll(c)}
                            className="text-[10px] text-emerald-300 hover:text-emerald-200 rounded-full border border-emerald-500/30 px-2 py-0.5 flex items-center gap-1"
                            data-testid={`committee-publish-all-${c.id}`}
                            title="Publish every unpublished meeting to the Documents library"
                          >
                            <Send className="h-2.5 w-2.5" />
                            Publish {unpub} unpublished
                          </button>
                        ) : null;
                      })()}
                    </div>
                    {(meetings[c.id] || []).length === 0 ? (
                      <div className="text-xs text-zinc-500 italic">No meetings logged yet. Click &quot;Meeting&quot; to add one.</div>
                    ) : (
                      <div className="space-y-2">
                        {(meetings[c.id] || []).map((m) => (
                          <div key={m.id} className="rounded-md border border-white/5 p-2.5" data-testid={`committee-meeting-${m.id}`}>
                            <div className="flex items-center justify-between gap-2">
                              <div className="min-w-0">
                                <div className="text-sm text-zinc-100 font-medium flex items-center gap-2">
                                  {m.title}
                                  {m.published_document_id && (
                                    <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 text-[9px]">
                                      Published
                                    </Badge>
                                  )}
                                </div>
                                <div className="text-[11px] text-zinc-500">
                                  {m.date} · by {m.created_by_name}
                                  {m.published_at && (
                                    <> · published {new Date(m.published_at).toLocaleDateString()}</>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => publishMeeting(c.id, m)}
                                  className="text-[10px] text-emerald-300 hover:text-emerald-200 rounded-full border border-emerald-500/30 px-2 py-0.5 flex items-center gap-1"
                                  data-testid={`committee-meeting-publish-${m.id}`}
                                  title={m.published_document_id ? "Re-publish PDF to Documents" : "Publish PDF to the Documents library"}
                                >
                                  <Send className="h-2.5 w-2.5" />
                                  {m.published_document_id ? "Re-publish" : "Publish"}
                                </button>
                                <button onClick={() => deleteMeeting(c.id, m.id)} className="text-zinc-500 hover:text-red-300"
                                        data-testid={`committee-meeting-delete-${m.id}`}>
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </div>
                            </div>
                            <div className="text-xs text-zinc-300 mt-1 whitespace-pre-wrap">{m.notes}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0a0a0b] border-white/10 text-white sm:max-w-[520px]" data-testid="committee-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Users2 className="h-4 w-4 text-orange-400" /> {editing ? "Edit committee" : "New committee"}</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">Pick a community, name the committee, and choose members from the current user list.</DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <div>
              <Label className="text-xs text-zinc-400">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     placeholder="e.g. Architectural Review Committee" className="bg-white/[0.04] border-white/10" data-testid="committee-name" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Community</Label>
              <Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v, chair_id: "", member_ids: [] })} disabled={!!editing}>
                <SelectTrigger className="bg-white/[0.04] border-white/10" data-testid="committee-property"><SelectValue placeholder="Choose community" /></SelectTrigger>
                <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
                  {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Description</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                        placeholder="What does this committee do?" className="bg-white/[0.04] border-white/10 text-sm" data-testid="committee-desc" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Chair (optional)</Label>
              <Select value={form.chair_id || "__none__"} onValueChange={(v) => setForm({ ...form, chair_id: v === "__none__" ? "" : v })}>
                <SelectTrigger className="bg-white/[0.04] border-white/10" data-testid="committee-chair"><SelectValue placeholder="No chair" /></SelectTrigger>
                <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
                  <SelectItem value="__none__">— No chair —</SelectItem>
                  {usersInScope.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Members</Label>
              <div className="max-h-48 overflow-y-auto rounded-md border border-white/10 p-2 space-y-1">
                {usersInScope.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
                    <input type="checkbox" checked={form.member_ids.includes(u.id)}
                           onChange={() => toggleMember(u.id)} className="accent-orange-500"
                           data-testid={`committee-member-${u.id}`} />
                    <span className="flex-1">{u.name}</span>
                    <Badge variant="outline" className="border-white/10 text-zinc-500 text-[10px]">{u.role}</Badge>
                  </label>
                ))}
                {usersInScope.length === 0 && <div className="text-[11px] text-zinc-500">No users in this community yet.</div>}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={busy} className="border-white/10">Cancel</Button>
            <Button onClick={submit} disabled={busy || !form.name || !form.property_id}
                    className="bg-orange-500 hover:bg-orange-600" data-testid="committee-save-btn">
              {busy ? "Saving…" : editing ? "Save" : "Create committee"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Meeting dialog */}
      <Dialog open={!!meetingDialog} onOpenChange={(v) => !v && setMeetingDialog(null)}>
        <DialogContent className="bg-[#0a0a0b] border-white/10 text-white sm:max-w-[520px]" data-testid="committee-meeting-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FilePlus2 className="h-4 w-4 text-indigo-400" /> New meeting note</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">{meetingDialog?.committee?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div>
              <Label className="text-xs text-zinc-400">Title</Label>
              <Input value={meetingForm.title} onChange={(e) => setMeetingForm({ ...meetingForm, title: e.target.value })}
                     placeholder="February 2026 Working Session" className="bg-white/[0.04] border-white/10" data-testid="meeting-title" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Date</Label>
              <Input type="date" value={meetingForm.date} onChange={(e) => setMeetingForm({ ...meetingForm, date: e.target.value })}
                     className="bg-white/[0.04] border-white/10" data-testid="meeting-date" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Notes</Label>
              <Textarea rows={7} value={meetingForm.notes} onChange={(e) => setMeetingForm({ ...meetingForm, notes: e.target.value })}
                        placeholder="Decisions, action items, motions passed…"
                        className="bg-white/[0.04] border-white/10 text-sm" data-testid="meeting-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMeetingDialog(null)} disabled={busy} className="border-white/10">Cancel</Button>
            <Button onClick={submitMeeting} disabled={busy || !meetingForm.title || !meetingForm.notes}
                    className="bg-indigo-500 hover:bg-indigo-600" data-testid="meeting-save-btn">
              {busy ? "Saving…" : "Save note"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
